import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../api.service';
import { CookieService } from 'ngx-cookie-service';
import { CommonService } from '../../shared/common.service';
import { HttpParams } from '@angular/common/http';

interface AuditTrailRecord {
  audit_id?: number;
  study_id?: string;
  subject_id?: string;
  visit_name?: string;
  form_name?: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  action: string;
  reason_for_change?: string;
  user_id?: string;
  user_role: string;
  timestamp: string;
  user_name?: string;
}

@Component({
  selector: 'app-audit-trail',
  templateUrl: './audit-trail.component.html',
  styleUrls: ['./audit-trail.component.scss'],
})
export class AuditTrailComponent implements OnInit {
  auditTrailData: AuditTrailRecord[] = [];
  filteredData: AuditTrailRecord[] = [];
  paginatedData: AuditTrailRecord[] = [];
  loading: boolean = false;
  
  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalPages = 0;
  totalRecords = 0;
  pageNumbersToShow: number = 4;
  pageRange: number[] = [];

  // Filters
  selectedStudyId: string = '';
  selectedStudyName: string = '';
  selectedSubjectId: string = '';
  selectedUserId: string = '';
  selectedAction: string = '';
  selectedFormName: string = '';
  startDate: string = '';
  endDate: string = '';
  
  // Study dropdown
  availableStudies: any[] = [];
  filteredStudies: any[] = [];
  isStudyDropdownOpen: boolean = false;
  studySearchText: string = '';

  // Subject dropdown
  availableSubjects: string[] = [];
  filteredSubjects: string[] = [];
  isSubjectDropdownOpen: boolean = false;
  subjectSearchText: string = '';

  // User dropdown
  availableUsers: any[] = [];
  filteredUsers: any[] = [];
  isUserDropdownOpen: boolean = false;
  userSearchText: string = '';

  // Action dropdown
  availableActions: string[] = ['ENTERED', 'MODIFIED', 'DELETED'];
  isActionDropdownOpen: boolean = false;

  // Form dropdown
  availableForms: string[] = [];
  filteredForms: string[] = [];
  isFormDropdownOpen: boolean = false;
  formSearchText: string = '';

  authToken: string | null = null;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private cookieService: CookieService,
    private commonService: CommonService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    let userData = this.commonService.getUserData();

    // Allow all authenticated users to access audit trail
    if (userData == null) {
      this.commonService.navigateTo('/');
    }

    this.authToken = this.cookieService.get('token');

    // Load available studies
    this.loadAvailableStudies();

    // Load initial data from query params
    this.route.queryParams.subscribe((params) => {
      this.selectedStudyId = params['studyId'] || '';
      this.selectedStudyName = params['studyName'] || '';
      this.selectedSubjectId = params['subjectId'] || '';
      this.selectedUserId = params['userId'] || '';
      this.selectedAction = params['action'] || '';
      this.selectedFormName = params['formName'] || '';
      this.startDate = params['startDate'] || '';
      this.endDate = params['endDate'] || '';
      this.currentPage = +params['page'] || 1;

      if (this.selectedStudyId) {
        this.loadAuditTrail();
        this.loadSubjectsForStudy(); // Load subjects from database
      }
    });
  }

  loadAvailableStudies(): void {
    const userData = this.commonService.getUserData();
    if (userData && userData.id) {
      // Use getAllStudiesWithCRC API (same as study-crc-list)
      this.apiService.getAllStudiesWithCRC(
        userData.id.toString(),
        1, // page
        1000, // limit - large number to get all studies
        '', // study_name filter - empty to get all
        '', // assigned_by filter - empty to get all
        '1', // is_active - only active studies
        '', // status filter - empty to get all statuses
        userData.designation || '' // designation
      ).subscribe(
        (response: any) => {
          console.log('✅ [loadAvailableStudies] getAllStudiesWithCRC response:', response);
          
          // Extract unique studies from response
          // Response can be { data: [...], totalRecords: ... } or just array
          let studiesArray: any[] = [];
          if (response && response.data && Array.isArray(response.data)) {
            studiesArray = response.data;
          } else if (Array.isArray(response)) {
            studiesArray = response;
          }
          
          // Get unique studies by study_id (since same study can appear multiple times for different sites)
          const uniqueStudiesMap = new Map();
          studiesArray.forEach((study: any) => {
            const studyId = study.study_id || study.id;
            if (studyId && !uniqueStudiesMap.has(studyId)) {
              uniqueStudiesMap.set(studyId, {
                id: studyId,
                study_id: studyId,
                name: study.study_name || study.name,
                study_name: study.study_name || study.name,
                code: study.code,
                status: study.status,
                template_name: study.template_name
              });
            }
          });
          
          this.availableStudies = Array.from(uniqueStudiesMap.values());
          this.filteredStudies = this.availableStudies;
          
          console.log('📊 [loadAvailableStudies] Processed unique studies:', this.availableStudies.length);
        },
        (error) => {
          console.error('Error loading studies:', error);
          this.availableStudies = [];
          this.filteredStudies = [];
        }
      );
    }
  }

  toggleStudyDropdown(): void {
    this.isStudyDropdownOpen = !this.isStudyDropdownOpen;
    if (this.isStudyDropdownOpen) {
      this.filterStudies();
    }
  }

  filterStudies(): void {
    if (!this.studySearchText) {
      this.filteredStudies = this.availableStudies;
      return;
    }
    const searchLower = this.studySearchText.toLowerCase();
    this.filteredStudies = this.availableStudies.filter(
      (study) =>
        (study.name || study.study_name || '')
          .toLowerCase()
          .includes(searchLower) ||
        (study.study_code || '')
          .toLowerCase()
          .includes(searchLower)
    );
  }

  selectStudy(study: any): void {
    this.selectedStudyId = study.id || study.study_id;
    this.selectedStudyName = study.name || study.study_name;
    this.isStudyDropdownOpen = false;
    this.studySearchText = '';
    this.loadAuditTrail();
    this.loadSubjectsForStudy();
    this.loadFormsForStudy();
  }

  toggleSubjectDropdown(): void {
    this.isSubjectDropdownOpen = !this.isSubjectDropdownOpen;
    if (this.isSubjectDropdownOpen) {
      this.filterSubjects();
    }
  }

  filterSubjects(): void {
    if (!this.subjectSearchText) {
      this.filteredSubjects = this.availableSubjects;
      return;
    }
    const searchLower = this.subjectSearchText.toLowerCase();
    this.filteredSubjects = this.availableSubjects.filter((subject) =>
      subject.toLowerCase().includes(searchLower)
    );
  }

  selectSubject(subject: string): void {
    this.selectedSubjectId = subject;
    this.isSubjectDropdownOpen = false;
    this.subjectSearchText = '';
    this.loadAuditTrail();
  }

  clearSubjectFilter(): void {
    this.selectedSubjectId = '';
    this.loadAuditTrail();
  }

  toggleUserDropdown(): void {
    this.isUserDropdownOpen = !this.isUserDropdownOpen;
    if (this.isUserDropdownOpen) {
      this.filterUsers();
    }
  }

  filterUsers(): void {
    if (!this.userSearchText) {
      this.filteredUsers = this.availableUsers;
      return;
    }
    const searchLower = this.userSearchText.toLowerCase();
    this.filteredUsers = this.availableUsers.filter(
      (user) =>
        (user.name || user.email || '')
          .toLowerCase()
          .includes(searchLower) ||
        (user.email || '')
          .toLowerCase()
          .includes(searchLower)
    );
  }

  selectUser(user: any): void {
    this.selectedUserId = user.id || user.user_id;
    this.isUserDropdownOpen = false;
    this.userSearchText = '';
    this.loadAuditTrail();
  }

  clearUserFilter(): void {
    this.selectedUserId = '';
    this.loadAuditTrail();
  }

  toggleActionDropdown(): void {
    this.isActionDropdownOpen = !this.isActionDropdownOpen;
  }

  selectAction(action: string): void {
    this.selectedAction = action;
    this.isActionDropdownOpen = false;
    this.loadAuditTrail();
  }

  clearActionFilter(): void {
    this.selectedAction = '';
    this.loadAuditTrail();
  }

  toggleFormDropdown(): void {
    this.isFormDropdownOpen = !this.isFormDropdownOpen;
    if (this.isFormDropdownOpen) {
      this.filterForms();
    }
  }

  filterForms(): void {
    if (!this.formSearchText) {
      this.filteredForms = this.availableForms;
      return;
    }
    const searchLower = this.formSearchText.toLowerCase();
    this.filteredForms = this.availableForms.filter((form) =>
      form.toLowerCase().includes(searchLower)
    );
  }

  selectForm(form: string): void {
    this.selectedFormName = form;
    this.isFormDropdownOpen = false;
    this.formSearchText = '';
    this.loadAuditTrail();
  }

  clearFormFilter(): void {
    this.selectedFormName = '';
    this.loadAuditTrail();
  }

  loadSubjectsForStudy(): void {
    if (!this.selectedStudyId) {
      this.availableSubjects = [];
      this.filteredSubjects = [];
      return;
    }

    console.log(`[loadSubjectsForStudy] Loading subjects for study_id: ${this.selectedStudyId}`);

    // Get all subjects from the study (regardless of audit trail data)
    this.apiService.getSubjectsForStudy(this.selectedStudyId).subscribe(
      (response: any) => {
        console.log(`[loadSubjectsForStudy] API response:`, response);
        
        // Check if response is valid (success can be true or undefined, data should be an array)
        if (response && Array.isArray(response.data)) {
          // Set subjects from database - these are all subjects regardless of audit trail data
          const dbSubjects = response.data || [];
          console.log(`[loadSubjectsForStudy] Subjects from database (${dbSubjects.length}):`, dbSubjects);
          
          // Also add any subjects from audit trail data that might not be in database
          // (in case there are subjects used but not in the database)
          const uniqueSubjects = new Set<string>(dbSubjects);
          this.auditTrailData.forEach((record) => {
            if (record.subject_id && record.subject_id.trim() !== '') {
              uniqueSubjects.add(record.subject_id);
            }
          });
          
          // Update with merged list (all subjects from database + any additional from audit trail)
          this.availableSubjects = Array.from(uniqueSubjects).sort();
          this.filteredSubjects = this.availableSubjects;
          console.log(`[loadSubjectsForStudy] Final available subjects (${this.availableSubjects.length}):`, this.availableSubjects);
        } else if (response && response.success === false) {
          // API returned an error response
          console.warn(`[loadSubjectsForStudy] API returned error:`, response.message);
          // Fallback: extract unique subjects from audit trail data
          this.populateSubjectsFromAuditTrail();
        } else {
          // Invalid response format
          console.warn(`[loadSubjectsForStudy] Invalid API response format:`, response);
          // Fallback: extract unique subjects from audit trail data
          this.populateSubjectsFromAuditTrail();
        }
      },
      (error) => {
        console.error('[loadSubjectsForStudy] Error loading subjects for study:', error);
        console.error('[loadSubjectsForStudy] Error details:', error);
        // On error, fallback to subjects from audit trail data
        this.populateSubjectsFromAuditTrail();
      }
    );
  }

  // Populate subjects from audit trail data (fallback method)
  private populateSubjectsFromAuditTrail(): void {
    const uniqueSubjects = new Set<string>();
    this.auditTrailData.forEach((record) => {
      if (record.subject_id && record.subject_id.trim() !== '') {
        uniqueSubjects.add(record.subject_id);
      }
    });
    this.availableSubjects = Array.from(uniqueSubjects).sort();
    this.filteredSubjects = this.availableSubjects;
    console.log(`[populateSubjectsFromAuditTrail] Subjects from audit trail (${this.availableSubjects.length}):`, this.availableSubjects);
  }

  loadFormsForStudy(): void {
    if (!this.selectedStudyId) {
      this.availableForms = [];
      return;
    }

    this.apiService.getFormsForStudy(this.selectedStudyId).subscribe(
      (response: any) => {
        if (response && response.success && Array.isArray(response.data)) {
          const uniqueForms = new Set<string>();

          // Add forms coming from the template
          response.data.forEach((formName: string) => {
            if (formName) uniqueForms.add(formName);
          });

          // Also include any forms already seen in audit trail data (for safety)
          this.auditTrailData.forEach((record) => {
            if (record.form_name) {
              uniqueForms.add(record.form_name);
            }
          });

          this.availableForms = Array.from(uniqueForms).sort();
          this.filteredForms = this.availableForms;
        } else {
          // Fallback: derive forms from audit trail data if API response is unexpected
          this.populateFormsFromAuditTrail();
        }
      },
      (error) => {
        console.error('[loadFormsForStudy] Error fetching forms for study:', error);
        // On error, fallback to forms from audit trail data
        this.populateFormsFromAuditTrail();
      }
    );
  }

  loadAuditTrail(): void {
    if (!this.selectedStudyId) {
      return;
    }

    this.loading = true;

    const params = new HttpParams()
      .set('study_id', this.selectedStudyId)
      .set('page', this.currentPage.toString())
      .set('limit', this.pageSize.toString())
      .set(
        'subject_id',
        this.selectedSubjectId ? this.selectedSubjectId : ''
      )
      .set('user_id', this.selectedUserId ? this.selectedUserId : '')
      .set('action', this.selectedAction ? this.selectedAction : '')
      .set('form_name', this.selectedFormName ? this.selectedFormName : '')
      .set('start_date', this.startDate ? this.startDate : '')
      .set('end_date', this.endDate ? this.endDate : '');

    this.apiService.getAuditTrail(params).subscribe(
      (response: any) => {
        if (response.success && response.data) {
          this.auditTrailData = response.data;
          this.filteredData = this.auditTrailData;
          this.totalRecords = response.pagination?.totalRecords || 0;
          this.totalPages = response.pagination?.totalPages || 0;

          // Update filter options
          this.loadSubjectsForStudy();
          this.loadFormsForStudy();

          // Backend already handles pagination, so use data directly
          this.paginatedData = this.auditTrailData;
          this.updatePageRange();
        } else {
          this.auditTrailData = [];
          this.filteredData = [];
          this.totalRecords = 0;
          this.totalPages = 0;
        }
        this.loading = false;
      },
      (error) => {
        console.error('Error fetching audit trail:', error);
        this.loading = false;
        alert('Error loading audit trail data. Please try again.');
      }
    );
  }

  // Populate forms from audit trail data (fallback method)
  private populateFormsFromAuditTrail(): void {
    const uniqueForms = new Set<string>();
    this.auditTrailData.forEach((record) => {
      if (record.form_name) {
        uniqueForms.add(record.form_name);
      }
    });
    this.availableForms = Array.from(uniqueForms).sort();
    this.filteredForms = this.availableForms;
    console.log(`[populateFormsFromAuditTrail] Forms from audit trail (${this.availableForms.length}):`, this.availableForms);
  }

  updatePaginatedData(): void {
    // Backend already handles pagination, so use data directly
    this.paginatedData = this.auditTrailData;
  }

  updatePageRange(): void {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    const maxPagesToShow = this.pageNumbersToShow;

    let startPage = Math.max(
      1,
      currentPage - Math.floor(maxPagesToShow / 2)
    );
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    this.pageRange = [];
    for (let i = startPage; i <= endPage; i++) {
      this.pageRange.push(i);
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadAuditTrail();
      this.updateQueryParams();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadAuditTrail();
      this.updateQueryParams();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadAuditTrail();
      this.updateQueryParams();
    }
  }

  updateQueryParams(): void {
    const queryParams: any = {
      studyId: this.selectedStudyId,
      studyName: this.selectedStudyName,
      page: this.currentPage,
    };

    if (this.selectedSubjectId) queryParams.subjectId = this.selectedSubjectId;
    if (this.selectedUserId) queryParams.userId = this.selectedUserId;
    if (this.selectedAction) queryParams.action = this.selectedAction;
    if (this.selectedFormName) queryParams.formName = this.selectedFormName;
    if (this.startDate) queryParams.startDate = this.startDate;
    if (this.endDate) queryParams.endDate = this.endDate;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadAuditTrail();
    this.updateQueryParams();
  }

  clearFilters(): void {
    this.selectedSubjectId = '';
    this.selectedUserId = '';
    this.selectedAction = '';
    this.selectedFormName = '';
    this.startDate = '';
    this.endDate = '';
    this.currentPage = 1;
    this.loadAuditTrail();
    this.updateQueryParams();
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return dateString;
    }
  }

  getActionBadgeClass(action: string): string {
    switch (action?.toUpperCase()) {
      case 'ENTERED':
      case 'INSERT': // Legacy support
        return 'badge-success';
      case 'MODIFIED':
      case 'UPDATE': // Legacy support
        return 'badge-warning';
      case 'DELETED':
      case 'DELETE': // Legacy support
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  getDisplayAction(action: string): string {
    if (!action) return 'N/A';
    const actionUpper = action.toUpperCase();
    // Map old action names to new ones for consistent display
    switch (actionUpper) {
      case 'INSERT':
        return 'ENTERED';
      case 'UPDATE':
        return 'MODIFIED';
      case 'DELETE':
        return 'DELETED';
      default:
        return action; // Return as-is if already new format or unknown
    }
  }

  downloadCSV(): void {
    if (!this.selectedStudyId || this.filteredData.length === 0) {
      alert(
        'No data available to download. Please select a study and ensure data is loaded.'
      );
      return;
    }

    try {
      // Prepare CSV header
      const headers = [
        'Audit ID',
        'Timestamp',
        'User Name',
        'User Role',
        'Action',
        'Study ID',
        'Subject ID',
        'Form Name',
        'Field Name',
        'Old Value',
        'New Value',
        'Reason for Change',
      ];

      // Prepare CSV rows
      const rows = this.filteredData.map((item) => [
        item.audit_id || 'N/A',
        this.formatDate(item.timestamp),
        item.user_name || item.user_id || 'N/A',
        item.user_role || 'N/A',
        item.action || 'N/A',
        item.study_id || 'N/A',
        item.subject_id || 'N/A',
        item.form_name || 'N/A',
        item.field_name || 'N/A',
        item.old_value || 'N/A',
        item.new_value || 'N/A',
        item.reason_for_change || 'N/A',
      ]);

      // Combine header and rows
      const csvContent = [headers, ...rows]
        .map((e) => e.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const fileName = `Audit_Trail_${this.selectedStudyName.replace(
        /[^a-zA-Z0-9]/g,
        '_'
      )}_${new Date().toISOString().split('T')[0]}.csv`;
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error generating CSV:', error);
      alert('Error generating CSV file. Please try again.');
    }
  }

  downloadExcel(): void {
    if (!this.selectedStudyId || this.filteredData.length === 0) {
      alert(
        'No data available to download. Please select a study and ensure data is loaded.'
      );
      return;
    }

    // For Excel, we'll use CSV format with .xlsx extension
    // In a production environment, you might want to use a library like xlsx
    this.downloadCSV();
  }

  getEndRecordNumber(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }

  isCRO(): boolean {
    const userData = this.commonService.getUserData();
    return userData && userData.designation === 'CRO';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown-container')) {
      this.isStudyDropdownOpen = false;
      this.isSubjectDropdownOpen = false;
      this.isUserDropdownOpen = false;
      this.isActionDropdownOpen = false;
      this.isFormDropdownOpen = false;
    }
  }
}

